import React, { useState, useEffect } from "react";

import { Container, Row, Col, Form } from "react-bootstrap";
import api from "../../api"
import ButtonLoader from "components/Common/ButtonLoader";
import { Link, useHistory } from "react-router-dom";
import { checkPermissionStatus } from "helpers/utils";
import { decryptDataFromArray, encryptDataToArray } from "helpers/EncDec";

import arrow_btn from "../../assets/images/icon/arrow-btn.svg";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  FetchLocationList,
  FetchRegionList,
  FetchStateList,
} from "services/locationService";
import { getUserToken } from "helpers/authHelper";
import { MultiSelect } from "react-multi-select-component";
import { useTranslation } from "react-i18next";
import { phoneValidationRules } from "helpers/common";

const CreateUser = props => {
  const { t } = useTranslation();
  const history = useHistory();
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [company, setCompany] = useState([]);
  const [vendor, setVendor] = useState([]);
  const [statePayload, setStatePayload] = useState({});

  const [CountryData, setCountryData] = useState([]);
  const [RegionData, setRegionData] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [StateData, setStateData] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [CityData, setCityData] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [LocationData, setLocationData] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [FranchiseData, setFranchiseData] = useState([]);
  const [selectedFranchises, setSelectedFranchises] = useState([]);
  const [isVendorVisible, setIsVendorVisible] = useState(false);
  const handleInput = event => {
    const { name, value } = event.target;

    // For phone field, only allow digits
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "");
      setStatePayload(prevState => ({
        ...prevState,
        [name]: digitsOnly,
      }));
      return;
    }

    setStatePayload(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Get country label by country ID
  const getCountryLabel = (countryId) => {
    const country = CountryData?.find(c => c.id === countryId || c.value === countryId);
    return country?.label || null;
  };

  // Validate phone number based on selected country
  const validatePhoneByCountry = (phone, countryId) => {
    if (!phone) {
      return { isValid: false, message: t(`Edit User.Validationmsg`) };
    }

    const countryLabel = getCountryLabel(countryId);
    const rule = phoneValidationRules[countryLabel] || phoneValidationRules.default;
    
    if (!rule.pattern.test(phone)) {
      return { isValid: false, message: rule.message };
    }

    return { isValid: true, message: "" };
  };

  // Get phone max length based on country
  const getPhoneMaxLength = (countryId) => {
    const countryLabel = getCountryLabel(countryId);
    const rule = phoneValidationRules[countryLabel] || phoneValidationRules.default;
    return rule.maxLength;
  };

  // Get phone placeholder based on country
  const getPhonePlaceholder = (countryId) => {
    const countryLabel = getCountryLabel(countryId);
    const placeholders = {
      "India": "e.g., 9876543210",
      "United States": "e.g., 1234567890",
      "United States of America": "e.g., 1234567890",
      "USA": "e.g., 1234567890",
      "United Kingdom": "e.g., 7911123456",
      "UK": "e.g., 7911123456",
      "United Arab Emirates": "e.g., 501234567",
      "UAE": "e.g., 501234567",
      "Saudi Arabia": "e.g., 512345678",
      "Canada": "e.g., 4161234567",
      "Australia": "e.g., 412345678",
      "Germany": "e.g., 15123456789",
      "France": "e.g., 612345678",
    };
    return placeholders[countryLabel] || "Enter mobile number";
  };

  const handleRoleSelection = roleId => {
    setStatePayload(prevState => ({
      ...prevState,
      role_id: roleId,
    }));
  };

  const handleCompanySelection = company_id => {
    let companyData = company?.find(c => c?.id === company_id);

    setStatePayload(prevState => ({
      ...prevState,
      company_id: company_id,
      country_id: companyData?.country_id,
    }));
  };

  const handleVendorSelection = vendor_id => {
    setStatePayload(prevState => ({
      ...prevState,
      vendor_id: vendor_id,
    }));
  };

  const createNewUser = payload => {
    api
      .post(
        `${process.env.REACT_APP_API_URL}/admin/user/create-user`,
        payload,
        {
        }
      )
      .then(response => {
        if (!response?.data?.status) {
          setIsLoading(false);
          const apiErrors = response?.data?.message?.errors;
          if (apiErrors && typeof apiErrors === "object") {
            setErrors(prev => ({ ...prev, ...apiErrors }));
            toast.error(Object.values(apiErrors)[0] || "An error occurred. Please check your input.");
          } else {
            toast.error(response?.data?.message || "An error occurred. Please check your input.");
          }
        } else {
          setIsLoading(false);
          toast.success(response?.data?.message);
          history.push("/user-list");
        }
      })
      .catch(err => {
        setIsLoading(false);
        const apiErrors = err.response?.data?.message?.errors;
        if (apiErrors && typeof apiErrors === "object") {
          setErrors(prev => ({ ...prev, ...apiErrors }));
          toast.error(Object.values(apiErrors)[0] || "An error occurred. Please try again.");
        } else {
          toast.error(err.response?.data?.message || "An error occurred. Please try again.");
        }
      });
  };

  const handleSubmit = event => {
    event.preventDefault();
    setErrors({});
    setIsLoading(true);

    let errorObj = {};

    // Catching and setting errors;
    let {
      password,
      confirm_password,
      role_id,
      company_id,
      country_id,
      vendor_id,
    } = statePayload;
    if (!statePayload?.firstname) {
      errorObj["firstname"] = t(`Edit User.Validationmsg`);
    }
    if (!statePayload?.lastname) {
      errorObj["lastname"] = t(`Edit User.Validationmsg`);
    }
    if (!statePayload?.email) {
      errorObj["email"] = t(`Edit User.Validationmsg`);
    }
    // Validate phone based on selected country
    const phoneValidation = validatePhoneByCountry(statePayload?.phone, statePayload?.country_id);
    if (!phoneValidation.isValid) {
      errorObj["phone"] = phoneValidation.message;
    }
    if (password?.length >= 6 && password !== confirm_password) {
      errorObj["password"] = "Passwords don't match!";
      errorObj["confirm_password"] = "Passwords don't match!";
    }

    if (password?.length < 6) {
      errorObj["password"] = "Password must be at least 6 characters!";
    }

    if (!role_id) {
      errorObj["role_id"] = "Please choose a valid role!";
    }

    if (!company_id) {
      errorObj["company_id"] = "Please select a company";
    }

    if (!country_id) {
      errorObj["country_id"] = "Please select a country";
    }
    if (isVendorVisible) {
      if (vendor_id == undefined || vendor_id == null || vendor_id == 0) {
        errorObj["vendor_id"] = t(`Create User.Please select a Vendor`);
      }
    }

    if (Object.keys(errorObj)?.length > 0) {
      setIsLoading(false);
      setErrors(prevState => ({
        ...prevState,
        ...errorObj,
      }));

      return;
    }

    const reqData = {
      firstname: statePayload?.firstname,
      lastname: statePayload?.lastname,
      email: statePayload?.email,
      phone: statePayload?.phone,
      role_id: statePayload?.role_id,
      username: statePayload?.username,
      password: statePayload?.password,
      company_id: statePayload?.company_id,
      country_id: statePayload?.country_id,
      region_ids: statePayload?.region_ids || [],
      state_ids: statePayload?.state_ids || [],
      city_ids: statePayload?.city_ids || [],
      location_ids: statePayload?.location_ids || [],
      franchise_ids: statePayload?.franchise_ids || [],
      vendorId: vendor_id,
      vendor_visible: isVendorVisible,
    };

    const encryptedQuery = encryptDataToArray(reqData);

    let payload = new FormData();
    payload.append("reqData", JSON.stringify(encryptedQuery));

    createNewUser(payload);
  };

  const fetchRoleData = () => {
    api
      .get(`${process.env.REACT_APP_API_URL}/admin/role/fetch-role`, {
      })
      .then(response => {
        const { success, allRoles } = response.data;
        if (success) {
          const { allRoles } = response.data;
          let data = allRoles?.map(item => ({
            id: item?.id,
            label: item?.name,
            value: item?.alias,
          }));
          setRoles(data);
        }
      })
      .catch(err => {});
  };
  const fetchCompanyData = () => {
    api
      .get(`${process.env.REACT_APP_API_URL}/admin/company/fetch-company`)
      .then(response => {
        let responseData = response?.data;
        responseData = decryptDataFromArray(responseData);

        const { success, allCompany } = responseData || {};

        if (success) {
          let data = allCompany?.map(item => ({
            id: item?.id,
            label: item?.company_name,
            value: item?.id,
            country_id: item?.country_id,
          }));
          let subdomainId = localStorage.getItem("subdomainId");

          if (subdomainId) {
            data = data?.filter(
              item => item?.id?.toString() === subdomainId?.toString()
            );
          }

          setCompany(data);

          if (data?.length === 1) {
            setStatePayload(prev => ({
              ...prev,
              company_id: data?.[0]?.id,
              country_id: data?.[0]?.country_id,
            }));
          }
        }
      })
      .catch(err => {});
  };

  const fetchVendorList = () => {
    api
      .get(`${process.env.REACT_APP_API_URL}/admin/vendor/vendorList`)
      .then(response => {
        let responseData = response?.data;
        responseData = decryptDataFromArray(responseData);

        const { success, allVendor } = responseData || {};

        if (success) {
          let data = allVendor?.map(item => ({
            id: item?.id,
            label: item?.vendor_name,
            value: item?.id,
          }));
          setVendor(data);
        }
      })
      .catch(err => {});
  };

  const fetchCountryData = () => {
    api
      .get(`${process.env.REACT_APP_API_URL}/admin/location/country-list`)
      .then(response => {
        const { success, allCountry } = response.data;
        if (success) {
          const { allCountry } = response.data;
          let data = allCountry?.map(item => ({
            id: item?.id,
            label: item?.name,
            value: item?.id,
          }));
          setCountryData(data);
        }
      })
      .catch(err => {});
  };
  const fetchRegionData = (company_id, franchiseIds) => {
    api({
      method: "get",
      url: `${process.env.REACT_APP_API_URL}/admin/location/region-list`,

     params: {
        franchise_id: franchiseIds?.length
          ? franchiseIds.join(",")   // "11,12,13"
          : null,
      },
    })
      .then(res => {
        let response = decryptDataFromArray(res?.data);
        const allRegion = response?.data || [];

        let data = allRegion.map(item => ({
          id: item.id,
          label: item.region,
          value: item.id,
          country_id: item.country_id,
        }));

        setRegionData(data);
         // clean previously selected regions
        setSelectedRegions(prevSelected =>
          prevSelected?.filter(selected =>
            data.some(region => region.value === selected.value)
          )
        );
      })
      .catch(err => {
        console.error("Region fetch error", err);
      });
  };

  const fetchStateData = (company_id, franchiseIds) => {
    api({
      method: "get",
      data: "",
      url: `${process.env.REACT_APP_API_URL}/admin/location/state-list`,
     params: {
        franchise_id: franchiseIds?.length
          ? franchiseIds.join(",")   // "11,12,13"
          : null,
      },
    })
      .then(res => {
        let response = decryptDataFromArray(res?.data);
        const StateList = response?.data;
        let data = StateList?.map(item => ({
          id: item?.id,
          label: item?.name,
          value: item?.id,
          country_id: item?.country_id,
          region_id: item?.region_id,
        }));
        setStateData(data);
         // clean previously selected states
        setSelectedStates(prevSelected =>
          prevSelected?.filter(selected =>
            data.some(state => state.value === selected.value)
          )
        );
      })
      .catch(err => {});
  };
  const fetchCityData = (company_id, franchiseIds) => {
    api
      .get(`${process.env.REACT_APP_API_URL}/admin/location/city-list`, {
        params: {
        franchise_id: franchiseIds?.length
          ? franchiseIds.join(",")   // "11,12,13"
          : null,
      },
      })
      .then(res => {
        let response = decryptDataFromArray(res?.data);
        const CityList = response?.data;
        let data = CityList?.map(item => ({
          id: item?.id,
          label: item?.city,
          value: item?.id,
          country_id: item?.country_id,
          region_id: item?.region_id,
          state_id: item?.state_id,
        }));
        setCityData(data);
         // clean previously selected cities
        setSelectedCities(prevSelected =>
          prevSelected?.filter(selected =>
            data.some(city => city.value === selected.value)
          )
        );
      })
      .catch(err => {});
  };
  const fetchLocationData = (company_id,franchiseIds) => {
    api({
      method: "get",
      data: "",
      url: `${process.env.REACT_APP_API_URL}/admin/location/location-list`,
      params: {
        franchise_id: franchiseIds?.length
          ? franchiseIds.join(",")   // "11,12,13"
          : null,
      },
    })
      .then(res => {
        let response = decryptDataFromArray(res?.data);
        const locationList = response?.data;
        let data = locationList?.map(item => ({
          id: item?.id,
          label: item?.label,
          value: item?.value,
          country_id: item?.country_id,
          region_id: item?.region_id,
          state_id: item?.state_id,
          city_id: item?.city_id,
        }));
        setLocationData(data);
         // clean previously selected locations
        setSelectedLocations(prevSelected =>
          prevSelected?.filter(selected =>
            data.some(location => location.value === selected.value)
          )
        );
      })
      .catch(err => {});
  };

  const fetchFranchiseData = company_id => {
    api({
      method: "get",
      data: "",
      url: `${process.env.REACT_APP_API_URL}/admin/Franchise/franchise-list`,
    })
      .then(res => {
        let response = decryptDataFromArray(res?.data);
        const franchiseList = response?.data?.filter(item => item.status === 1) || [];
        let data = franchiseList?.map(item => ({
          id: item?.id,
          label: item?.franchise_name,
          value: item?.id,
          region_ids: item?.region_ids || [],
          state_ids: item?.state_ids || [],
          city_ids: item?.city_ids || [],
          location_ids: item?.location_ids || [],
        }));
        setFranchiseData(data);
      })
      .catch(err => {});
  };

  const getFilteredFranchises = () => {
    if (
      !statePayload?.region_ids?.length &&
      !statePayload?.state_ids?.length &&
      !statePayload?.city_ids?.length &&
      !statePayload?.location_ids?.length
    ) {
      return [];
    }

    return FranchiseData.filter(franchise => {
      // Check if franchise matches any selected region
      const matchesRegion =
        !statePayload?.region_ids?.length ||
        franchise?.region_ids?.some(regionId =>
          statePayload?.region_ids?.includes(regionId)
        );

      // Check if franchise matches any selected state
      const matchesState =
        !statePayload?.state_ids?.length ||
        franchise?.state_ids?.some(stateId =>
          statePayload?.state_ids?.includes(stateId)
        );

      // Check if franchise matches any selected city
      const matchesCity =
        !statePayload?.city_ids?.length ||
        franchise?.city_ids?.some(cityId =>
          statePayload?.city_ids?.includes(cityId)
        );

      // Check if franchise matches any selected location/area
      const matchesLocation =
        !statePayload?.location_ids?.length ||
        franchise?.location_ids?.some(locationId =>
          statePayload?.location_ids?.includes(locationId)
        );

      // Franchise matches if it matches at least one selected location type
      return matchesRegion || matchesState || matchesCity || matchesLocation;
    });
  };

  const handleSelectAllRegions = () => {
    setSelectedRegions(RegionData);

    let regionIds = RegionData?.map(reg => reg?.id);
    setStatePayload(prev => ({
      ...prev,
      region_ids: regionIds,
    }));
  };
  const handleDeselectAllRegions = () => {
    setSelectedRegions([]);
    setSelectedStates([]);
    setSelectedCities([]);
    setSelectedLocations([]);
    setSelectedFranchises([]);

    setStatePayload(prev => ({
      ...prev,
      region_ids: [],
      state_ids: [],
      city_ids: [],
      location_ids: [],
      franchise_ids: [],
    }));
  };
  const handleChangeRegions = regions => {
    if (!regions?.length) {
      handleDeselectAllRegions();

      return;
    }

    setSelectedRegions(regions);

    let regionIds = regions?.map(reg => reg?.id);

    let tempStates = selectedStates?.filter(state =>
      regionIds?.includes(state?.region_id)
    );
    setSelectedStates(tempStates);

    let stateIds = tempStates?.map(state => state?.id);
    let tempCities = selectedCities?.filter(
      city =>
        regionIds?.includes(city?.region_id) &&
        stateIds?.includes(city?.state_id)
    );
    setSelectedCities(tempCities);

    let cityIds = tempCities?.map(city => city?.id);
    let tempLocations = selectedLocations?.filter(
      loc =>
        regionIds?.includes(loc?.region_id) &&
        stateIds?.includes(loc?.state_id) &&
        cityIds?.includes(loc?.city_id)
    );
    setSelectedLocations(tempLocations);

    let locationIds = tempLocations?.map(loc => loc?.id);

    // Filter selected franchises based on new location selection
    // let filteredFranchises = selectedFranchises?.filter(franchise => {
    //   const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
    //   if (!franchiseData) return false;
    //   return (
    //     regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
    //     stateIds?.some(id => franchiseData?.state_ids?.includes(id)) ||
    //     cityIds?.some(id => franchiseData?.city_ids?.includes(id)) ||
    //     locationIds?.some(id => franchiseData?.location_ids?.includes(id))
    //   );
    // });
    // setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      region_ids: regionIds,
      state_ids: stateIds,
      city_ids: cityIds,
      location_ids: locationIds,
      // franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };

  const handleSelectAllStates = () => {
    let tempStateData = StateData;

    if (!!statePayload?.region_ids?.length) {
      tempStateData = tempStateData?.filter(state =>
        statePayload?.region_ids?.includes(state?.region_id)
      );
    }

    tempStateData = tempStateData?.filter(
      state => state?.country_id === statePayload?.country_id
    );
    setSelectedStates(tempStateData);

    let stateIds = tempStateData?.map(state => state?.id);

    setStatePayload(prev => ({
      ...prev,
      state_ids: stateIds,
    }));
  };
  const handleDeselectAllStates = () => {
    setSelectedStates([]);
    setSelectedCities([]);
    setSelectedLocations([]);
    // Filter selected franchises to keep only those matching remaining regions
    let filteredFranchises = selectedFranchises?.filter(franchise => {
      const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
      if (!franchiseData) return false;
      const regionIds = statePayload?.region_ids || [];
      return regionIds?.some(id => franchiseData?.region_ids?.includes(id));
    });
    setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      state_ids: [],
      city_ids: [],
      location_ids: [],
      franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };
  const handleChangeStates = states => {
    if (!states?.length) {
      handleDeselectAllStates();

      return;
    }

    setSelectedStates(states);

    let stateIds = states?.map(state => state?.id);
    let tempCities = selectedCities?.filter(city =>
      stateIds?.includes(city?.state_id)
    );
    setSelectedCities(tempCities);

    let cityIds = tempCities?.map(city => city?.id);
    let tempLocations = selectedLocations?.filter(
      loc =>
        stateIds?.includes(loc?.state_id) && cityIds?.includes(loc?.city_id)
    );
    setSelectedLocations(tempLocations);

    let locationIds = tempLocations?.map(loc => loc?.id);

    // Filter selected franchises based on new location selection
    // let filteredFranchises = selectedFranchises?.filter(franchise => {
    //   const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
    //   if (!franchiseData) return false;
    //   const regionIds = statePayload?.region_ids || [];
    //   return (
    //     regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
    //     stateIds?.some(id => franchiseData?.state_ids?.includes(id)) ||
    //     cityIds?.some(id => franchiseData?.city_ids?.includes(id)) ||
    //     locationIds?.some(id => franchiseData?.location_ids?.includes(id))
    //   );
    // });
    // setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      state_ids: stateIds,
      city_ids: cityIds,
      location_ids: locationIds,
      //  franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };

  const handleSelectAllCities = () => {
    let tempCityData = CityData;

    if (!!statePayload?.region_ids?.length) {
      tempCityData = tempCityData?.filter(city =>
        statePayload?.region_ids?.includes(city?.region_id)
      );
    }

    tempCityData = tempCityData?.filter(
      city =>
        city?.country_id === statePayload?.country_id &&
        statePayload?.state_ids?.includes(city?.state_id)
    );
    setSelectedCities(tempCityData);

    let cityIds = tempCityData?.map(reg => reg?.id);

    setStatePayload(prev => ({
      ...prev,
      city_ids: cityIds,
    }));
  };
  const handleDeselectAllCities = () => {
    setSelectedCities([]);
    setSelectedLocations([]);
    // Filter selected franchises to keep only those matching remaining regions/states
    let filteredFranchises = selectedFranchises?.filter(franchise => {
      const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
      if (!franchiseData) return false;
      const regionIds = statePayload?.region_ids || [];
      const stateIds = statePayload?.state_ids || [];
      return (
        regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
        stateIds?.some(id => franchiseData?.state_ids?.includes(id))
      );
    });
    setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      city_ids: [],
      location_ids: [],
      franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };
  const handleChangeCities = cities => {
    if (!cities?.length) {
      handleDeselectAllCities();

      return;
    }

    setSelectedCities(cities);

    let cityIds = cities?.map(city => city?.id);
    let tempLocations = selectedLocations?.filter(loc =>
      cityIds?.includes(loc?.city_id)
    );
    setSelectedLocations(tempLocations);

    let locationIds = tempLocations?.map(loc => loc?.id);

    // Filter selected franchises based on new location selection
    // let filteredFranchises = selectedFranchises?.filter(franchise => {
    //   const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
    //   if (!franchiseData) return false;
    //   const regionIds = statePayload?.region_ids || [];
    //   const stateIds = statePayload?.state_ids || [];
    //   return (
    //     regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
    //     stateIds?.some(id => franchiseData?.state_ids?.includes(id)) ||
    //     cityIds?.some(id => franchiseData?.city_ids?.includes(id)) ||
    //     locationIds?.some(id => franchiseData?.location_ids?.includes(id))
    //   );
    // });
    // setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      city_ids: cityIds,
      location_ids: locationIds,
      // franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };

  const handleSelectAllLocations = () => {
    let tempLocationData = LocationData;

    if (!!statePayload?.region_ids?.length) {
      tempLocationData = tempLocationData?.filter(loc =>
        statePayload?.region_ids?.includes(loc?.region_id)
      );
    }

    tempLocationData = tempLocationData?.filter(
      loc =>
        loc?.country_id === statePayload?.country_id &&
        statePayload?.state_ids?.includes(loc?.state_id) &&
        statePayload?.city_ids?.includes(loc?.city_id)
    );
    setSelectedLocations(tempLocationData);

    let locationIds = tempLocationData?.map(loc => loc?.id);

    setStatePayload(prev => ({
      ...prev,
      location_ids: locationIds,
    }));
  };
  const handleDeselectAllLocations = () => {
    setSelectedLocations([]);
    // Filter selected franchises to keep only those matching remaining regions/states/cities
    let filteredFranchises = selectedFranchises?.filter(franchise => {
      const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
      if (!franchiseData) return false;
      const regionIds = statePayload?.region_ids || [];
      const stateIds = statePayload?.state_ids || [];
      const cityIds = statePayload?.city_ids || [];
      return (
        regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
        stateIds?.some(id => franchiseData?.state_ids?.includes(id)) ||
        cityIds?.some(id => franchiseData?.city_ids?.includes(id))
      );
    });
    setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      location_ids: [],
      franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };
  const handleChangeLocations = locations => {
    if (!locations?.length) {
      handleDeselectAllLocations();

      return;
    }

    setSelectedLocations(locations);

    let locationIds = locations?.map(loc => loc?.id);

    // Filter selected franchises based on new location selection
    let filteredFranchises = selectedFranchises?.filter(franchise => {
      const franchiseData = FranchiseData.find(f => f?.id === franchise?.id);
      if (!franchiseData) return false;
      const regionIds = statePayload?.region_ids || [];
      const stateIds = statePayload?.state_ids || [];
      const cityIds = statePayload?.city_ids || [];
      return (
        regionIds?.some(id => franchiseData?.region_ids?.includes(id)) ||
        stateIds?.some(id => franchiseData?.state_ids?.includes(id)) ||
        cityIds?.some(id => franchiseData?.city_ids?.includes(id)) ||
        locationIds?.some(id => franchiseData?.location_ids?.includes(id))
      );
    });
    setSelectedFranchises(filteredFranchises);

    setStatePayload(prev => ({
      ...prev,
      location_ids: locationIds,
      franchise_ids: filteredFranchises?.map(f => f?.id) || [],
    }));
  };

  const handleChangeFranchises = franchises => {
    setSelectedFranchises(franchises);
    let franchiseIds = franchises?.map(franchise => franchise?.id);
    setStatePayload(prev => ({
      ...prev,
      franchise_ids: franchiseIds,
    }));
  };

  useEffect(() => {
    fetchCountryData();
    fetchRoleData();
    fetchCompanyData();
    fetchVendorList();
    return () => {};
  }, []);

  useEffect(() => {
    if (!!statePayload?.company_id) {
      fetchFranchiseData(statePayload?.company_id);
      fetchRegionData(statePayload?.company_id);
      fetchStateData(statePayload?.company_id);
      fetchCityData(statePayload?.company_id);
      fetchLocationData(statePayload?.company_id);
    }

    return () => {};
  }, [statePayload?.company_id]);

  console.log(selectedFranchises,"selectedFranchises")

  useEffect(() => {
    if (statePayload?.company_id) {
      const franchiseId = selectedFranchises?.length
        ? selectedFranchises.map(f=>f.id)
        : null;

      fetchRegionData(statePayload.company_id, franchiseId);
    }
  }, [statePayload?.company_id, selectedFranchises]);

  useEffect(() => {
    if (statePayload?.company_id) {
      const franchiseId = selectedFranchises?.length
        ? selectedFranchises.map(f=>f.id)
        : null;

      fetchStateData(statePayload.company_id, franchiseId);
    }
  }, [statePayload?.company_id, selectedFranchises]);

   useEffect(() => {
    if (statePayload?.company_id) {
      const franchiseId = selectedFranchises?.length
        ? selectedFranchises.map(f=>f.id)
        : null;

      fetchCityData(statePayload.company_id, franchiseId);
    }
  }, [statePayload?.company_id, selectedFranchises]);

    useEffect(() => {
    if (statePayload?.company_id) {
      const franchiseId = selectedFranchises?.length
        ? selectedFranchises.map(f=>f.id)
        : null;

      fetchLocationData(statePayload.company_id, franchiseId);
    }
  }, [statePayload?.company_id, selectedFranchises]);
  
  useEffect(() => {
    const theme = localStorage.getItem("company_theme");
    if (theme) {
      document.body.classList.add(`theme-${theme}`);
    }
  }, []);

  if (!checkPermissionStatus("user-list", "create")) {
    return (
      <div className="page-content">
        <h4 className="text-center">{t(`Create User.Access Denied`)}</h4>
      </div>
    );
  }


  return (
    <div className="page-content">
      <Container fluid>
        <div className="joblist-container create-vacancy">
          <Row className="mb-3 align-items-center py-2 pt-0 depart-content">
            <Col lg="6" md="6">
              <Link to="/user-list">
                <h5 className="text-uppercase heading-one">
                  <img src={arrow_btn} alt="arrow_btn" />
                  {t(`Create User.Create User`)}
                </h5>
              </Link>
            </Col>
          </Row>
        </div>

        <div className="outerTab-box-wrapper below-vacancy">
          <div className="form-details-section">
            <div class="form-details-section heading pb-3">
              <h2 className="sub-heading-text mb-2">
                {t(`Create User.Personal Details`)}:
              </h2>
            </div>

            <Row>
              <Form.Group className="col-md-3 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.First Name`)}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder=" "
                  name="firstname"
                  value={statePayload?.firstname}
                  onChange={handleInput}
                  title={`${t(`Create User.First Name`)}`}
                />
                {errors?.firstname && (
                  <small className="text-danger"> {errors?.firstname} </small>
                )}
              </Form.Group>

              <Form.Group className="col-md-3 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Last Name`)}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder=" "
                  name="lastname"
                  value={statePayload?.lastname}
                  onChange={handleInput}
                  title={`${t(`Create User.Last Name`)}`}
                />
                {errors?.lastname && (
                  <small className="text-danger"> {errors?.lastname} </small>
                )}
              </Form.Group>

              <Form.Group className="col-md-3 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Email`)}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder=" "
                  name="email"
                  value={statePayload?.email}
                  onChange={handleInput}
                  title={`${t(`Create User.Email`)}`}
                />
                {errors?.email && (
                  <small className="text-danger"> {errors?.email} </small>
                )}
              </Form.Group>

              <Form.Group className="col-md-3 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Phone No`)}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder={getPhonePlaceholder(statePayload?.country_id)}
                  name="phone"
                  value={statePayload?.phone}
                  onChange={handleInput}
                  title={`${t(`Create User.Phone No`)}`}
                  maxLength={getPhoneMaxLength(statePayload?.country_id)}
                />
                {errors?.phone && (
                  <small className="text-danger"> {errors?.phone} </small>
                )}
              </Form.Group>
             {/* <Form.Group className="col-md-12 mb-3 floating-label">
                <Form.Check
                  id={2}
                  name={`vendor`}
                  type="switch"
                  title={`Please cheked ${t(
                    `Create User.Is Vendor`
                  ).toLowerCase()}`}
                  className="h6"
                  label={t(`Create User.Is Vendor`)}
                  checked={isVendorVisible}
                  onChange={e => {
                    const isChecked = e.target.checked;
                    setIsVendorVisible(isChecked);
                    if (!isChecked) {
                      setStatePayload(prevState => ({
                        ...prevState,
                        vendor_id: 0,
                      }));
                    }
                  }}
                />
              </Form.Group>
              */}
            </Row>
            {isVendorVisible && (
              <Row>
                <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                  <Form.Label className="float-label">
                    {t(`Create User.Vendor`)}
                    <span className="text-danger">*</span>
                  </Form.Label>
                  <select
                    type="select"
                    name="vendor_id"
                    className="col-md-4 mb-1 form-control form-select"
                    style={{ width: "100%" }}
                    onChange={e => handleVendorSelection(e.target?.value)}
                    value={statePayload?.vendor_id}
                    onMouseEnter={e => {
                      e.target.setAttribute(
                        "title",
                        e.target.options[e.target.selectedIndex].text
                      );
                    }}
                  >
                    <option value="" selected>
                      {t(`Create User.Select Vendor`)}
                    </option>

                    {vendor &&
                      vendor.map(el => (
                        <option key={el?.value} value={el?.id}>
                          {el?.label}
                        </option>
                      ))}
                  </select>

                  {errors?.vendor_id && (
                    <small className="text-danger"> {errors?.vendor_id} </small>
                  )}
                </Form.Group>
              </Row>
            )}
            <br />

            <h2 className="sub-heading-text mb-3 pb-2">
              {t(`Create User.Login Details`)}:
            </h2>

            <Row>
              <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Company`)}
                  <span className="text-danger">*</span>
                </Form.Label>
                <select
                  type="select"
                  name="company_id"
                  className="col-md-4 mb-1 form-control form-select"
                  style={{ width: "100%" }}
                  onChange={e => handleCompanySelection(e.target?.value)}
                  value={statePayload?.company_id}
                  disabled={company?.length === 1}
                  title={
                    document.querySelector("select")?.selectedOptions[0]
                      ?.text || "Create User.Company"
                  }
                >
                  <option selected disabled>
                    {t(`Create User.Select Company`)}
                  </option>

                  {company &&
                    company.map(el => (
                      <option key={el?.value} value={el?.id}>
                        {el?.label}
                      </option>
                    ))}
                </select>

                {errors?.company_id && (
                  <small className="text-danger"> {errors?.company_id} </small>
                )}
              </Form.Group>

              <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Country`)}
                  <span className="text-danger">*</span>
                </Form.Label>
                <select
                  type="select"
                  name="country_id"
                  className="col-md-4 mb-1 form-control form-select"
                  style={{ width: "100%" }}
                  value={statePayload?.country_id}
                  disabled
                  title={
                    CountryData.find(el => el?.id === statePayload?.country_id)
                      ?.label || t(`Create User.Country`)
                  }
                >
                  {CountryData &&
                    CountryData.map(el => (
                      <option key={el?.value} value={el?.id}>
                        {el?.label}
                      </option>
                    ))}
                </select>

                {errors?.country_id && (
                  <small className="text-danger"> {errors?.country_id} </small>
                )}
              </Form.Group>

              <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">{"Dealership"}</Form.Label>

                <div
                  title={
                    FranchiseData.length
                      ? `${FranchiseData.map(
                          option => option.label || option.name
                        ).join(", ")}`
                      : `${"Dealership"}`
                  }
                >
                  <MultiSelect
                    options={FranchiseData}
                    value={selectedFranchises}
                    onChange={handleChangeFranchises}
                    overrideStrings={{
                      selectSomeItems: "Select Dealership",
                    }}
                  />
                </div>
              </Form.Group>

              <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Region`)}
                </Form.Label>

                <div
                  title={
                    selectedRegions.length
                      ? `${selectedRegions
                          .map(option => option.label || option.name)
                          .join(", ")}`
                      : `${t("Create User.Region")}`
                  }
                >
                  <MultiSelect
                    // isMulti
                    options={[
                      ...RegionData?.filter(
                        region =>
                          !statePayload?.country_id ||
                          region?.country_id === statePayload?.country_id
                      ),
                    ]}
                    value={selectedRegions}
                    onChange={handleChangeRegions}
                    overrideStrings={{
                      selectSomeItems: t(`Create User.Select Region`),
                    }}
                  />
                </div>
              </Form.Group>

              <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.State`)}
                </Form.Label>

                <div
                  title={
                    selectedStates.length
                      ? `${selectedStates
                          .map(option => option.label || option.name)
                          .join(", ")}`
                      : `${t("Create User.State")}`
                  }
                >
                  <MultiSelect
                    // isMulti
                    options={[
                      ...StateData?.filter(
                        state =>
                          !statePayload?.country_id ||
                          (!statePayload?.region_ids?.length &&
                            state?.country_id === statePayload?.country_id) ||
                          (state?.country_id === statePayload?.country_id &&
                            statePayload?.region_ids?.includes(
                              state?.region_id
                            ))
                      ),
                    ]}
                    value={selectedStates}
                    onChange={handleChangeStates}
                    overrideStrings={{
                      selectSomeItems: t(`Create User.Select State`),
                    }}
                  />
                </div>
              </Form.Group>

              <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.City`)}
                </Form.Label>

                <div
                  title={
                    selectedCities.length
                      ? `${selectedCities
                          .map(option => option.label || option.name)
                          .join(", ")}`
                      : `${t("Create User.City")}`
                  }
                >
                  <MultiSelect
                    // isMulti
                    options={[
                      // {
                      //   isDisabled: true,
                      //   value: null,
                      //   label: (
                      //     <div className="d-flex gap-2">
                      //       <button
                      //         className="selectall-btn"
                      //         onClick={handleSelectAllCities}
                      //       >
                      //         Select All
                      //       </button>
                      //       <button
                      //         className="selectall-btn"
                      //         onClick={handleDeselectAllCities}
                      //       >
                      //         Deselect All
                      //       </button>
                      //     </div>
                      //   ),
                      // },
                      ...CityData?.filter(
                        city =>
                          !statePayload?.country_id ||
                          (!statePayload?.region_ids?.length &&
                            city?.country_id === statePayload?.country_id &&
                            statePayload?.state_ids?.includes(
                              city?.state_id
                            )) ||
                          (city?.country_id === statePayload?.country_id &&
                            statePayload?.region_ids?.includes(
                              city?.region_id
                            ) &&
                            statePayload?.state_ids?.includes(city?.state_id))
                      ),
                    ]}
                    value={selectedCities}
                    onChange={handleChangeCities}
                    overrideStrings={{
                      selectSomeItems: t(`Create User.Select City`),
                    }}
                  />
                </div>
              </Form.Group>

              <Form.Group className="col-md-4 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Area`)}
                </Form.Label>

                <div
                  title={
                    selectedLocations.length
                      ? `${selectedLocations
                          .map(option => option.label || option.name)
                          .join(", ")}`
                      : `${t("Create User.Area")}`
                  }
                >
                  <MultiSelect
                    // isMulti
                    options={[
                      // {
                      //   isDisabled: true,
                      //   value: null,
                      //   label: (
                      //     <div className="d-flex gap-2">
                      //       <button
                      //         className="selectall-btn"
                      //         onClick={handleSelectAllLocations}
                      //       >
                      //         Select All
                      //       </button>
                      //       <button
                      //         className="selectall-btn"
                      //         onClick={handleDeselectAllLocations}
                      //       >
                      //         Deselect All
                      //       </button>
                      //     </div>
                      //   ),
                      // },
                      ...LocationData?.filter(
                        loc =>
                          !statePayload?.country_id ||
                          (!statePayload?.region_ids?.length &&
                            loc?.country_id === statePayload?.country_id &&
                            statePayload?.state_ids?.includes(loc?.state_id) &&
                            statePayload?.city_ids?.includes(loc?.city_id)) ||
                          (loc?.country_id === statePayload?.country_id &&
                            statePayload?.region_ids?.includes(
                              loc?.region_id
                            ) &&
                            statePayload?.state_ids?.includes(loc?.state_id) &&
                            statePayload?.city_ids?.includes(loc?.city_id))
                      ),
                    ]}
                    value={selectedLocations}
                    onChange={handleChangeLocations}
                    overrideStrings={{
                      selectSomeItems: t(`Create User.Select Area`),
                    }}
                  />
                </div>
              </Form.Group>
            </Row>

            <Row>
              <Form.Group className="col-md-3 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Role`)}
                  <span className="text-danger">*</span>
                </Form.Label>
                <select
                  type="select"
                  name="saas_parent"
                  className="col-md-6 mb-1 form-control form-select"
                  style={{ width: "100%" }}
                  onChange={e => handleRoleSelection(e.target?.value)}
                  onMouseEnter={e => {
                    e.target.setAttribute(
                      "title",
                      e.target.options[e.target.selectedIndex].text
                    );
                  }}
                >
                  <option selected disabled>
                    {t(`Create User.Select Role`)}
                  </option>
                  {roles &&
                    roles.map(el => {
                      return (
                        <option key={el?.value} value={el?.id}>
                          {el?.label}
                        </option>
                      );
                    })}
                </select>
                {errors?.role_id && (
                  <small className="text-danger"> {errors?.role_id} </small>
                )}
              </Form.Group>

              <Form.Group className="col-md-3 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Username`)}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder=" "
                  autocomplete="off"
                  name="username"
                  value={statePayload?.username}
                  onChange={handleInput}
                  title={`${t(`Create User.Username`)}`}
                />
                {errors?.username && (
                  <small className="text-danger"> {errors?.username} </small>
                )}
              </Form.Group>

              <Form.Group className="col-md-3 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Password`)}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="password"
                  placeholder=" "
                  name="password"
                  autocomplete="off"
                  value={statePayload?.password}
                  onChange={handleInput}
                  title={`${t(`Create User.Password`)}`}
                />
                {errors?.password && (
                  <small className="text-danger"> {errors?.password} </small>
                )}
              </Form.Group>

              <Form.Group className="col-md-3 col-sm-6 mb-3 floating-label">
                <Form.Label className="float-label">
                  {t(`Create User.Confirm Password`)}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="password"
                  placeholder=" "
                  name="confirm_password"
                  value={statePayload?.confirm_password}
                  onChange={handleInput}
                  title={`${t(`Create User.Confirm Password`)}`}
                />
                {errors?.confirm_password && (
                  <small className="text-danger">
                    {" "}
                    {errors?.confirm_password}{" "}
                  </small>
                )}
              </Form.Group>
            </Row>

            <Form.Group className="cta text-center d-flex gap-3 justify-content-start mt-4 pb-3">
              <ButtonLoader
                title={t(`Create User.Cancel`)}
                variant="secondary"
                type="submit"
                onClick={e => {
                  e.preventDefault();
                  history.push("/user-list");
                }}
              />
              <ButtonLoader
                title={t(`Create User.Save Details`)}
                variant="primary"
                type="submit"
                loading={isLoading}
                message={"Saving"}
                onClick={e => {
                  handleSubmit(e);
                }}
              />
            </Form.Group>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default CreateUser;
